import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  useMediaQuery,
  Dialog,
  Typography,
  Button,
  DialogTitle,
  DialogContentText,
  Box,
  Grid,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import ACSnackbar from './../../ACSnackbar';
import { useAccount } from './../../../contexts/Account';
import {
  endAdvisorFreeTrialEarly,
  getAdvisorRecurlyPlan,
} from './../../../services/advisor_services';
import eventBus, { eventBusValues } from './../../../eventBus';
import { SnackbarType } from './../../../services/utils/types';
import {
  sendFSEventOnUpgrade,
  sendFSEventUpgradeViewed,
} from '../../../services/FullStory';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import PlanToggle from './PlanToggle';

const useStyles = ({ isXS }) => ({
  dialogContainer: {
    borderRadius: {xxs: '0px', xs: '10px'},
    boxShadow: '0 5px 20px rgba(0, 0, 0, 0.07)',
    padding: { xxs: '4px', sm: '16px' },
    textAlign: 'center',
    width: { xxs: '100%', sm: '900px' },
    overflow: { xxs: 'scroll', sm: 'hidden' },
  },
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingLeft: isXS ? '10px' : '50px',
    paddingRight: isXS ? '10px' : '50px',
  },
  textHeadline: {
    fontWeight: 600,
    fontSize: { xxs: '12px', sm: '20px' },
  },
  bluePrice: {
    color: '#3171F6',
    fontWeight: 900,
    fontSize: { xxs: '16px', sm: '24px' },
  },
  priceLabel: {
    fontWeight: 400,
    fontSize: { xxs: '10px', sm: '12px' },
    color: '#454545',
    justifyContent: 'center',
  },
  featureText: {
    mx: '4px',
    color: '#454545',
    fontWeight: 700,
    fontSize: { xxs: '10px', sm: '12px' },
    height: '40px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    alignItems: 'center',
    textAlign: { xxs: 'end', sm: 'start' },
  },
  colHeader: {
    height: '140px',
    paddingTop: { xxs: '16px', sm: '10px' },
  },
  colBorder: {
    border: '1px solid #C8C8C8',
    borderRadius: '5px',
    backgroundColor: '#FFFFFF8F', // 56% opacity
  },
  upgradeButton: {
    my: 1,
    borderRadius: '40px',
    width: { xxs: '70px', md: '150px' },
    height: { xxs: '24px', md: '40px' },
    fontWeight: 700,
    fontSize: { xxs: '10px', md: '15px' },
    padding: '0px 0px',
  },
});

const errorCodes = {
  recurlyError: 'RECURLY_ERROR',
  dynamoError: 'DYNAMO_DB_ERROR',
  salesforceError: 'SALESFORCE_ERROR',
};

let outsideSaveAdvisor = null;

const ToggleFreeTrialModal = () => {
  const { t } = useTranslation();
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles({ isXS });
  const [fromBanner, setFromBanner] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackbarSuccess, setSnackbarSuccess] = useState(false);
  const [snackbarError, setSnackbarError] = useState(false);
  const [annualToggle, setAnnualToggle] = useState(false);
  const [earlyConversionPlan, setEarlyConversionPlan] = useState(false);
  const [monthlyPlan2, setMonthlyPlan2] = useState(false);
  const [annualPlan2, setAnnualPlan2] = useState(false);
  const {
    accountDetails,
    editAccountInformation,
    updateUserPlan,
    asLoggedUserDetails,
    type,
    currentUserInfo,
    currentPlan,
  } = useAccount();
  const { search } = useLocation();

  const [selectedPlan, setSelectedPlan] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const [earlyResponse, monthlyResponse, annualResponse] =
        await Promise.all([
          getAdvisorRecurlyPlan({
            recurlyPlanCode: currentPlan.earlyConversionAndUpgradePlancode,
          }),
          getAdvisorRecurlyPlan({
            recurlyPlanCode: currentPlan.b2cFreeTrailMonthlyUpgrade2,
          }),
          getAdvisorRecurlyPlan({
            recurlyPlanCode: currentPlan.b2cFreeTrailAnnualUpgrade2,
          }),
        ]);

      if (earlyResponse.data && earlyResponse.data.getRecurlyPlan) {
        setEarlyConversionPlan(earlyResponse.data.getRecurlyPlan);
      }
      if (monthlyResponse.data && monthlyResponse.data.getRecurlyPlan) {
        setMonthlyPlan2(monthlyResponse.data.getRecurlyPlan);
      }
      if (annualResponse.data && annualResponse.data.getRecurlyPlan) {
        setAnnualPlan2(annualResponse.data.getRecurlyPlan);
      }
    }

    eventBus.on(eventBusValues.triggerAdvisorUpgradeAction, (data) => {
      if (data) {
        setFromBanner(data.fromBanner || false);
        outsideSaveAdvisor = data.saveChanges || null;
      } else {
        setFromBanner(false);
      }
      setOpenModal(true);
    });

    const profileParam = new URLSearchParams(search).get('ft-pop-up');
    if (profileParam === 'banner') {
      setOpenModal(true);
    } else if (profileParam === 'gate') {
      setOpenModal(true);
    }

    fetchData();

    return () => {
      eventBus.remove(eventBusValues.triggerAdvisorUpgradeAction);
    };
  }, []);

  function handleClose() {
    setOpenModal(false);
    setSelectedPlan({});
    outsideSaveAdvisor = null;
  }

  const sendFSEvent = (success, errorMessage = '', error = null) => {
    sendFSEventOnUpgrade({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      newPlan: selectedPlan,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'freeTrial',
      success,
      errorMessage: success ? null : errorMessage,
      error: success ? null : error,
    });
  };

  const sendFSViewedEvent = (upgradeAttempted = false) => {
    sendFSEventUpgradeViewed({
      accountDetails,
      currentPlan: currentPlan,
      asLoggedUserDetails,
      currentUserInfo,
      source: fromBanner ? 'banner' : 'gate',
      type,
      upgradeType: 'free_trial',
      upgradeAttempted,
    });
  };

  async function upgradeCall() {
    sendFSViewedEvent(true);
    if (selectedPlan && !loading) {
      setLoading(true);
      if (outsideSaveAdvisor) {
        await outsideSaveAdvisor();
        outsideSaveAdvisor = null;
      }
      endAdvisorFreeTrialEarly({
        ADVISOR_ID: accountDetails.id,
        PLAN_CODE: selectedPlan.recurlyPlanCode,
      })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.endAdvisorFreeTrialEarly
          ) {
            const newAdvisor = response.data.endAdvisorFreeTrialEarly;
            setSnackMessage(t('FREE_TRIAL_CONVERTION_SUCCESS'));
            setSnackbarSuccess(true);
            setLoading(false);
            editAccountInformation(
              {
                freeTrialEndsOn: newAdvisor.freeTrialEndsOn,
                recurlyPlanCode: newAdvisor.recurlyPlanCode,
              },
              true
            );
            if (selectedPlan.recurlyPlanCode !== currentPlan.recurlyPlanCode) {
              updateUserPlan(selectedPlan);
            }
            sendFSEvent(true);
            handleClose();
          }
        })
        .catch((e) => {
          let errText = '';
          try {
            const err = JSON.parse(e.errors[0].message);
            if (err.code === errorCodes.recurlyError) {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_RECURLY');
            } else if (err.code === errorCodes.dynamoError) {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_DYNAMO');
            } else if (err.code === errorCodes.salesforceError) {
              setSnackMessage(t('FREE_TRIAL_CONVERTION_SUCCESS'));
              setSnackbarSuccess(true);
              return;
            } else {
              errText = t('FREE_TRIAL_CONVERTION_ERROR_UNEXP');
            }
            setLoading(false);
            handleClose();
          } catch (err) {
            errText = t('FREE_TRIAL_CONVERTION_ERROR_UNEXP');
            setLoading(false);
          }
          sendFSEvent(false, errText, e);
          setSnackMessage(errText);
          setSnackbarError(true);
        });
    } else {
      setSnackMessage('Select an upgrade option to continue');
      setSnackbarSuccess(true);
      sendFSEvent(false, 'no plan selected', 'no plan selected');
    }
  }

  function getAnualSavePercentage() {
    try {
      const monthlyPerYear = currentPlan?.recurlyPlanCodePrice * 12;
      return (
        ((monthlyPerYear - earlyConversionPlan?.recurlyPlanCodePrice) /
          monthlyPerYear) *
        100
      );
    } catch {
      return 0;
    }
  }

  function getMonthlySave() {
    try {
      const monthlyPerYear = currentPlan?.recurlyPlanCodePrice * 12;
      const monthly2PerYear = monthlyPlan2?.recurlyPlanCodePrice * 12;
      return monthly2PerYear - monthlyPerYear;
    } catch {
      return 0;
    }
  }

  const repeatedBox = ({ content, key, customsx = {} }) => (
    <Typography
      key={key}
      sx={{
        ...classes.featureText,
        ...classes.priceLabel,
        ...customsx,
        ':last-child': { borderBottom: 'none' },
      }}
    >
      {content}
    </Typography>
  );

  return (
    <>
      <Dialog
        open={openModal}
        fullScreen={isXS}
        onClose={() => {
          if (loading) return;
          handleClose();
          sendFSViewedEvent();
        }}
        PaperProps={{
          id: 'freeTrialUpgradeModal',
          sx: classes.dialogContainer,
        }}
      >
        {isXS && (
          <Close
            onClick={() => {
              if (loading) return;
              handleClose();
              sendFSViewedEvent();
            }}
            sx={{
              position: 'absolute',
              top: '12px',
              right: '8px',
              cursor: 'pointer',
              color: 'black',
              fontSize: '24px',
              zIndex: 2,
            }}
          />
        )}
        <DialogTitle
          sx={{
            fontSize: { xxs: '22px', sm: '28px' },
            fontWeight: 800,
            letterSpacing: '-0.43px',
            lineHeight: { xxs: '20px', sm: '24px' },
            padding: { xxs: '8px 12px', sm: '8px 24px' },
            marginTop: { xxs: '28px', sm: '8px' },
            zIndex: 2,
          }}
        >
          Upgrade to Unlock Full Access
        </DialogTitle>
        <DialogContentText
          sx={{
            fontSize: { xxs: '15px', sm: '16px' },
            fontWeight: 500,
            letterSpacing: '-0.43px',
            padding: { xxs: '0px 36px', sm: '0' },
            lineHeight: { xxs: '20px', sm: '24px' },
            marginTop: { xxs: '12px', sm: '0px' },
            zIndex: 2,
          }}
        >
          Most clients upgrade to an annual plan to show their commitment
        </DialogContentText>
        <Box
          sx={{
            mt: 2,
            width: '100%',
            textAlign: 'center',
            zIndex: 2,
          }}
        >
          <Grid container>
            <Grid item xxs={12}>
              <PlanToggle
                value={annualToggle}
                setter={setAnnualToggle}
                containerProps={{
                  sx: { display: { xxs: 'block', sm: 'none' }, mb: 4 },
                }}
              />
            </Grid>
            <Grid item xxs={3} sm={4}>
              <Box sx={classes.colHeader}>
                <Typography
                  sx={{
                    ...classes.textHeadline,
                    fontSize: { xxs: '16px', sm: '20px' },
                  }}
                >
                  Compare Plans
                </Typography>
                <PlanToggle
                  value={annualToggle}
                  setter={setAnnualToggle}
                  containerProps={{
                    sx: { display: { xxs: 'none', sm: 'block' } },
                  }}
                />
                <Typography
                  sx={{
                    ...classes.textHeadline,
                    fontWeight: 900,
                    marginTop: { xxs: '20px', sm: '18px' },
                  }}
                >
                  Features
                </Typography>
              </Box>

              <Typography sx={classes.featureText}>
                # of boards you can join
              </Typography>
              <Typography sx={classes.featureText}>
                AdvisoryCloud Profile
              </Typography>
              <Typography sx={classes.featureText}>
                Custom rates & services
              </Typography>
              <Typography sx={classes.featureText}>
                Show boards you're committed
              </Typography>
              <Typography sx={classes.featureText}>
                Featured profile on AdvisoryCloud
              </Typography>
              <Typography sx={classes.featureText}>
                Personalized recommendations
              </Typography>
              <Typography sx={classes.featureText}>
                Professional roadmap
              </Typography>
              <Typography sx={classes.featureText}>
                Dedicated account manager
              </Typography>
            </Grid>
            <Grid item xxs={3} sm={2.666} px={{ xxs: '0px', xs: '4px' }}>
              <Box sx={classes.colBorder}>
                <Box sx={classes.colHeader}>
                  <Typography sx={classes.textHeadline}>Trial</Typography>
                  <Typography sx={classes.bluePrice}>Free</Typography>
                  <Typography sx={classes.priceLabel}>
                    ${currentPlan?.recurlyPlanCodePrice}/mo after{' '}
                    {currentPlan?.lengthOfTrialInDays} days
                  </Typography>
                  <Button
                    id="currentUpgradeButton"
                    className="ftUpgradeCTA"
                    variant="outlined"
                    size="small"
                    sx={{
                      ...classes.upgradeButton,
                      cursor: 'initial',
                      '&:hover': {
                        backgroundColor: 'white',
                      },
                    }}
                    onClick={() => {}}
                  >
                    Current Plan
                  </Button>
                </Box>
                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  1 board
                </Typography>
                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  Private
                </Typography>
                {Array.from({ length: 6 }, (_, i) =>
                  repeatedBox({ content: '-', key: i })
                )}
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666} px={{ xxs: '0px', xs: '4px' }}>
              <Box sx={classes.colBorder}>
                <Box sx={classes.colHeader}>
                  <Typography sx={classes.textHeadline}>
                    {
                      (annualToggle ? earlyConversionPlan : currentPlan)
                        ?.salesforceValues?.tier
                    }
                  </Typography>
                  <Typography sx={classes.bluePrice}>
                    $
                    {annualToggle
                      ? (earlyConversionPlan?.recurlyPlanCodePrice / 12).toFixed(
                          0
                        )
                      : currentPlan?.recurlyPlanCodePrice}{' '}
                    <Typography component="span" sx={classes.priceLabel}>
                      /mo
                    </Typography>
                  </Typography>
                  <Typography sx={classes.priceLabel}>
                    {annualToggle
                      ? `$${earlyConversionPlan?.recurlyPlanCodePrice} paid anually`
                      : `Or save ${getAnualSavePercentage().toFixed(
                          0
                        )}% anually`}
                  </Typography>
                  <Button
                    id="annualUpgradeButton"
                    className="ftUpgradeCTA"
                    variant="contained"
                    size="small"
                    sx={classes.upgradeButton}
                    onClick={() => {
                      if (annualToggle) setSelectedPlan(earlyConversionPlan);
                      else setSelectedPlan(currentPlan);
                      setOpenConfirmationModal(true);
                    }}
                    disabled={loading}
                  >
                    {loading &&
                    [
                      earlyConversionPlan.recurlyPlanCode,
                      currentPlan.recurlyPlanCode,
                    ].includes(selectedPlan.recurlyPlanCode) ? (
                      <LoadingLogoSkeleton
                        width={{ xxs: '20px', sm: '28px' }}
                        height={{ xxs: '20px', sm: '28px' }}
                      />
                    ) : (
                      'Upgrade'
                    )}
                  </Button>
                </Box>
                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  {annualToggle ? 'Unlimited' : '1 board'}
                </Typography>
                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  Published
                </Typography>

                {Array.from({ length: annualToggle ? 3 : 1 }, (_, i) =>
                  repeatedBox({ content: <Check />, key: i })
                )}
                {Array.from({ length: annualToggle ? 3 : 5 }, (_, i) =>
                  repeatedBox({ content: '-', key: i })
                )}
              </Box>
            </Grid>
            <Grid item xxs={3} sm={2.666} px={{ xxs: '0px', xs: '4px' }}>
              <Box sx={classes.colBorder}>
                <Box sx={classes.colHeader}>
                  <Typography sx={classes.textHeadline}>
                    {
                      (annualToggle ? annualPlan2 : monthlyPlan2)
                        ?.salesforceValues?.tier
                    }
                  </Typography>
                  <Typography sx={classes.bluePrice}>
                    $
                    {annualToggle
                      ? (annualPlan2?.recurlyPlanCodePrice / 12).toFixed(0)
                      : monthlyPlan2?.recurlyPlanCodePrice}{' '}
                    <Typography component="span" sx={classes.priceLabel}>
                      /mo
                    </Typography>
                  </Typography>
                  <Typography sx={classes.priceLabel}>
                    {annualToggle
                      ? `$${annualPlan2?.recurlyPlanCodePrice} paid anually`
                      : `Or save $${getMonthlySave()} anually`}
                  </Typography>
                  <Button
                    id="vipUpgradeButton"
                    className="ftUpgradeCTA"
                    variant="contained"
                    size="small"
                    sx={classes.upgradeButton}
                    onClick={() => {
                      if (annualToggle) setSelectedPlan(annualPlan2);
                      else setSelectedPlan(monthlyPlan2);
                      setOpenConfirmationModal(true);
                    }}
                    disabled={loading}
                  >
                    {loading &&
                    [
                      annualPlan2.recurlyPlanCode,
                      monthlyPlan2.recurlyPlanCode,
                    ].includes(selectedPlan.recurlyPlanCode) ? (
                      <LoadingLogoSkeleton
                        width={{ xxs: '20px', sm: '28px' }}
                        height={{ xxs: '20px', sm: '28px' }}
                      />
                    ) : (
                      'Upgrade'
                    )}
                  </Button>
                </Box>

                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  Unlimited
                </Typography>
                <Typography
                  sx={{ ...classes.featureText, ...classes.priceLabel }}
                >
                  Optimized
                </Typography>

                {Array.from({ length: 5 }, (_, i) =>
                  repeatedBox({ content: <Check />, key: i })
                )}
                {repeatedBox({
                  content: 'Quarterly check-ins',
                  key: null,
                  customsx: {
                    textAlign: 'center',
                    padding: { xxs: '0 4px', sm: '0' },
                  },
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Typography
          sx={{
            fontSize: { xxs: '12px', sm: '16px' },
            mt: '8px',
            fontWeight: 500,
            color: '#232B35',
            zIndex: 2,
          }}
        >
          When you click Upgrade your card will be charged and you will gain full
          access!
        </Typography>
        <Box
          sx={{
            width: { xxs: '240px', sm: '286px' },
            height: { xxs: '240px', sm: '286px' },
            borderRadius: '50%',
            background: '#FFEED6',
            position: 'absolute',
            bottom: { xxs: '-30px', sm: '-145px' },
            left: '-112px',
            zIndex: 1,
            overflow: 'hidden',
          }}
        />
        <Box
          sx={{
            width: { xxs: '200px', sm: '300px' },
            height: { xxs: '200px', sm: '300px' },
            borderRadius: '50%',
            background: '#DBF7EB',
            position: 'absolute',
            top: { xxs: '-88px', sm: '-150px' },
            right: { xxs: '-80px', sm: '-150px' },
            zIndex: 1,
            overflow: 'hidden',
          }}
        />
      </Dialog>
      <Dialog
        open={openConfirmationModal}
        onClose={() => setOpenConfirmationModal(false)}
        PaperProps={{
          id: 'freeTrialUpgradeConfirmationModal',
          sx: { borderRadius: 3, minWidth: 320, textAlign: 'center' },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xxs: '22px', sm: '32px' },
            padding: { xxs: '8px 12px', sm: '8px 24px' },
          }}
        >
          Confirm Upgrade
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to upgrade to the{' '}
            <b>
              {annualToggle ? 'Annual' : 'Monthly'}{' '}
              {selectedPlan?.salesforceValues?.tier}
            </b>{' '}
            plan?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            id="cancelUpgradeButton"
            variant="outlined"
            onClick={() => setOpenConfirmationModal(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            id="confirmUpgradeButton"
            variant="contained"
            onClick={() => {
              setOpenConfirmationModal(false);
              upgradeCall();
            }}
            disabled={loading}
          >
            {loading ? <LoadingLogoSkeleton /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <ACSnackbar
        open={snackbarSuccess}
        text={snackMessage || ''}
        severity={SnackbarType.SUCCESS}
        onClose={() => {
          setSnackbarSuccess(false);
        }}
        autoHideDuration={6000}
      />
      <ACSnackbar
        open={snackbarError}
        text={snackMessage || ''}
        severity={SnackbarType.ERROR}
        onClose={() => {
          setSnackbarError(false);
        }}
        autoHideDuration={6000}
      />
    </>
  );
};

export default ToggleFreeTrialModal;
